/* eslint-disable max-lines */
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import {
  Languages,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink,
  Loader2,
  Info,
  Cpu
} from 'lucide-react';
import PageHeader from '@components/layout/page-header';
import { useGetAdminTranslation } from '@utils/api/admin/use-get-admin-translation';
import { useUpdateAdminTranslation } from '@utils/api/admin/use-update-admin-translation';
import { useTestAdminTranslation } from '@utils/api/admin/use-test-admin-translation';
import toast from 'react-hot-toast';

const DEEPL_API_URL = 'https://api.deepl.com';
const DEEPL_FREE_API_URL = 'https://api-free.deepl.com';
const DEEPL_PRICING_URL = 'https://www.deepl.com/pro-api';

/**
 * On-Prem Admin Translation Settings Page
 * Configure translation services (DeepL) for on-premises deployment.
 * Translation is disabled by default - customers must explicitly configure it.
 */
export default function AdminTranslation() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const {
    data,
    isLoading: settingsLoading,
    isError: settingsError
  } = useGetAdminTranslation();
  const updateMutation = useUpdateAdminTranslation();
  const testMutation = useTestAdminTranslation();

  const settings = data?.result;

  // Form state
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [apiUrl, setApiUrl] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  // Derived state - use form state if modified, otherwise use API data
  const isEnabled = enabled !== null ? enabled : (settings?.enabled ?? false);
  const currentApiUrl =
    apiUrl || settings?.deepl_config?.api_url || DEEPL_API_URL;

  // Track if form has been modified
  const hasChanges = useMemo(() => {
    if (!settings) return false;
    return (
      (enabled !== null && enabled !== settings.enabled) ||
      apiKey !== '' ||
      (apiUrl !== '' && apiUrl !== settings.deepl_config?.api_url)
    );
  }, [enabled, apiKey, apiUrl, settings]);

  const handleSave = () => {
    const payload: {
      enabled?: boolean;
      deepl_token?: string;
      deepl_url?: string;
    } = {};

    if (enabled !== null) {
      payload.enabled = enabled;
    }
    if (apiKey) {
      payload.deepl_token = apiKey;
    }
    if (apiUrl && apiUrl !== settings?.deepl_config?.api_url) {
      payload.deepl_url = apiUrl;
    }

    updateMutation.mutate(payload, {
      onSuccess: () => {
        // Reset form state after successful save
        setEnabled(null);
        setApiKey('');
        setApiUrl('');
        setShowApiKey(false);
      },
      onError: (error: unknown) => {
        const err = error as {
          response?: {
            data?: {
              error_description?: string;
              detail?: string;
              message?: string;
            };
          };
        };
        const msg =
          err?.response?.data?.error_description ||
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          t('an-error-occurred');
        toast.error(msg);
      }
    });
  };

  const handleTest = () => {
    testMutation.mutate(undefined, {
      onSuccess: response => {
        if (response.result?.success) {
          toast.success(t('translation-connection-successful'));
        } else {
          toast.error(
            response.result?.message || t('translation-connection-failed')
          );
        }
      },
      onError: () => {
        toast.error(t('translation-connection-failed'));
      }
    });
  };

  const getStatusClass = (status: string | undefined) => {
    switch (status) {
      case 'configured':
        return 'text-green-500 dark:text-green-400';
      case 'error':
        return 'text-red-500 dark:text-red-400';
      default:
        return 'text-gray-500 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status: string | undefined) => {
    switch (status) {
      case 'configured':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="bg-app min-h-screen px-4 py-2">
      <PageHeader
        title={t('translation-settings')}
        caption={t('configure-translation-services-for-on-premises')}
      />

      {settingsLoading && (
        <div
          className="flex items-center gap-2 py-8 text-sm"
          style={{ color: getColour('inactiveText') }}
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('loading')}
        </div>
      )}

      {settingsError && (
        <p className="text-sm text-red-500 dark:text-red-400 py-4">
          {t('an-error-occurred')}
        </p>
      )}

      <div
        className="space-y-6"
        style={
          settingsLoading || settingsError
            ? { opacity: 0.5, pointerEvents: 'none' }
            : undefined
        }
      >
        {/* Info Banner */}
        <div className="rounded-2xl border border-blue-500/50 bg-blue-500/5 dark:bg-blue-500/10 p-4 flex items-start gap-3">
          <Info className="h-5 w-5 flex-shrink-0 mt-0.5 text-blue-500" />
          <div>
            <p className="text-sm" style={{ color: getColour('normalText') }}>
              {t('translation-deepl-info')}
            </p>
            <a
              href={DEEPL_PRICING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm mt-2 hover:underline text-blue-500"
            >
              {t('view-deepl-api-pricing')}
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* Current Status Card */}
        <section
          className="rounded-2xl border p-6"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Languages
                className="h-5 w-5"
                style={{ color: getColour('inactiveText') }}
              />
              <h2
                className="text-sm font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {t('translation-status')}
              </h2>
            </div>
            {getStatusIcon(settings?.status)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span
                className="text-xs uppercase tracking-wide"
                style={{ color: getColour('inactiveText') }}
              >
                {t('status')}
              </span>
              <p
                className={`text-sm font-medium mt-1 ${getStatusClass(settings?.status)}`}
              >
                {settings?.status === 'configured'
                  ? t('configured')
                  : settings?.status === 'error'
                    ? t('error')
                    : t('not-configured')}
              </p>
            </div>
            <div>
              <span
                className="text-xs uppercase tracking-wide"
                style={{ color: getColour('inactiveText') }}
              >
                {t('provider')}
              </span>
              <p
                className="text-sm font-medium mt-1"
                style={{ color: getColour('normalText') }}
              >
                {settings?.provider === 'deepl'
                  ? 'DeepL'
                  : settings?.provider || '-'}
              </p>
            </div>
            <div>
              <span
                className="text-xs uppercase tracking-wide"
                style={{ color: getColour('inactiveText') }}
              >
                {t('last-successful-translation')}
              </span>
              <p
                className="text-sm font-medium mt-1"
                style={{ color: getColour('normalText') }}
              >
                {settings?.last_successful_translation
                  ? new Date(
                      settings.last_successful_translation
                    ).toLocaleString()
                  : '-'}
              </p>
            </div>
          </div>
        </section>

        {/* DeepL Configuration */}
        <section
          className="rounded-2xl border p-6"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-2xl flex items-center justify-center bg-[#0f2b46]">
              <span className="text-white font-bold text-sm">DL</span>
            </div>
            <div>
              <h2
                className="text-sm font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {t('deepl-configuration')}
              </h2>
              <p
                className="text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('configure-deepl-api-for-translation')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label
                  className="text-sm font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('enable-translation')}
                </label>
                <p
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('enable-translation-description')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEnabled(!isEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isEnabled ? 'bg-blue-500' : ''}`}
                style={
                  isEnabled
                    ? undefined
                    : { backgroundColor: getColour('thirdLayer') }
                }
              >
                <span
                  // eslint-disable-next-line no-restricted-syntax
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* API Key */}
            <div>
              <label
                className="block text-xs font-medium uppercase tracking-wide mb-2"
                style={{ color: getColour('inactiveText') }}
              >
                {t('api-key')}
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  className="w-full px-3 py-2 pr-10 rounded-2xl text-sm"
                  style={{
                    backgroundColor: getColour('thirdLayer'),
                    border: `1px solid ${getColour('border')}`,
                    color: getColour('normalText')
                  }}
                  placeholder={
                    settings?.deepl_config?.api_key_masked ||
                    t('enter-deepl-api-key')
                  }
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: getColour('inactiveText') }}
                >
                  {showApiKey ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {settings?.deepl_config?.api_key_masked && !apiKey && (
                <p
                  className="text-xs mt-1"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('current-key')}: {settings.deepl_config.api_key_masked}
                </p>
              )}
            </div>

            {/* API URL */}
            <div>
              <label
                className="block text-xs font-medium uppercase tracking-wide mb-2"
                style={{ color: getColour('inactiveText') }}
              >
                {t('api-url')}
              </label>
              <select
                className="w-full px-3 py-2 rounded-2xl text-sm"
                style={{
                  backgroundColor: getColour('thirdLayer'),
                  border: `1px solid ${getColour('border')}`,
                  color: getColour('normalText')
                }}
                value={currentApiUrl}
                onChange={e => setApiUrl(e.target.value)}
              >
                <option value={DEEPL_API_URL}>
                  {DEEPL_API_URL} ({t('pro-tier')})
                </option>
                <option value={DEEPL_FREE_API_URL}>
                  {DEEPL_FREE_API_URL} ({t('free-tier')})
                </option>
              </select>
              <p
                className="text-xs mt-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('deepl-url-description')}
              </p>
            </div>

            {/* Test Connection & Save Buttons */}
            <div className="flex items-center gap-3 pt-4">
              <button
                type="button"
                onClick={handleTest}
                disabled={
                  testMutation.isLoading ||
                  (settings?.status === 'not_configured' && !apiKey)
                }
                className="px-4 py-2 rounded-2xl text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: getColour('thirdLayer'),
                  border: `1px solid ${getColour('border')}`,
                  color: getColour('normalText')
                }}
              >
                {testMutation.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                {t('test-connection')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!hasChanges || updateMutation.isLoading}
                className={`px-4 py-2 rounded-2xl text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${hasChanges ? 'bg-blue-500 text-white' : ''}`}
                style={
                  hasChanges
                    ? undefined
                    : {
                        backgroundColor: getColour('thirdLayer'),
                        color: getColour('inactiveText')
                      }
                }
              >
                {updateMutation.isLoading && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {t('save-changes')}
              </button>
            </div>
          </div>
        </section>

        {/* vLLM Placeholder (Coming Soon) */}
        <section
          className="rounded-2xl border p-6 opacity-60"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className="h-10 w-10 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <Cpu
                className="h-5 w-5"
                style={{ color: getColour('inactiveText') }}
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2
                  className="text-sm font-semibold"
                  style={{ color: getColour('normalText') }}
                >
                  {t('local-translation')}
                </h2>
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400">
                  {t('coming-soon')}
                </span>
              </div>
              <p
                className="text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('local-translation-description')}
              </p>
            </div>
          </div>
          <p className="text-sm" style={{ color: getColour('inactiveText') }}>
            {t('vllm-translation-info')}
          </p>
        </section>
      </div>
    </div>
  );
}
